import { animalApiIds } from '@/components/Animals/AnimalsTable/BenchlingWorklist.utils';
import { successToast } from '@/helpers';
import { _isNotBlank } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { StudyApiId } from '@/model/Study.model';
import { useBenchlingDelegatedCall } from '@/support/benchlingLink/useBenchlingDelegatedCall';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { type FC, useRef } from 'react';
import { useForm } from 'react-hook-form';

export interface CreateBenchlingWorklistProps {
  studyApiId: StudyApiId;
  animals: Array<Animal>;
  closeModal: () => void;
}

interface BenchlingWorklistFormData {
  name: string;
}

const CreateBenchlingWorklist: FC<CreateBenchlingWorklistProps> = ({ studyApiId, animals, closeModal }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<BenchlingWorklistFormData>({
    mode: 'onChange',
    defaultValues: { name: '' },
  });

  const created = useRef(false);
  const createWorklist = useBenchlingDelegatedCall({
    endpoint: 'POST /api/v1/studies/{studyApiId}/benchling-worklists',
    actionLabel: 'create this worklist',
    slug: 'benchling-worklist-create',
    // Nothing in this modal can help until the user has linked, so get it out of their way.
    onFailure: { benchling_link_required: closeModal },
  });

  const onSubmit = async (data: BenchlingWorklistFormData): Promise<void> => {
    // The modal can still take an Enter press between creating and closing; a second worklist would be
    // a copy the user has to clean up in Benchling.
    if (created.current) {
      return;
    }
    const result = await createWorklist.invoke({
      path: { studyApiId },
      body: { name: data.name.trim(), animal_api_ids: animalApiIds(animals) },
    });
    if (result.ok) {
      created.current = true;
      successToast(`Created Benchling worklist "${result.body.name}" with ${result.body.item_count} animals`);
      closeModal();
    }
  };

  const fieldError = errors.name?.message;

  return (
    <ModalContainer size="narrow" testKey="create-benchling-worklist">
      <ModalHeader title="Create Benchling worklist" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="pa3">
          <p className="mb3 f6 mid-gray">
            {animals.length} {animals.length === 1 ? 'animal' : 'animals'} will be added, attributed to you.
          </p>

          <label htmlFor="benchling-worklist-name" className="db mb1 f6">
            Worklist name
          </label>
          <input
            id="benchling-worklist-name"
            type="text"
            className={_isNotBlank(fieldError) ? 'input__error' : ''}
            placeholder="Enter a name for this worklist..."
            autoFocus
            data-testid="create-benchling-worklist__name"
            {...register('name', {
              validate: (value) => (_isNotBlank(value?.trim()) ? true : 'A worklist name is required.'),
            })}
          />
          {_isNotBlank(fieldError) && (
            <small className="red db" data-testid="create-benchling-worklist__error">
              {fieldError}
            </small>
          )}
        </div>

        <ModalActions
          submitBtnText="Create worklist"
          submitButtonProps={{ disabled: createWorklist.loading || !isValid, loading: createWorklist.loading }}
          onSubmit={handleSubmit(onSubmit)}
          onCancel={closeModal}
          className="pa3 bt b--moon-gray"
        />
      </form>
    </ModalContainer>
  );
};

export default CreateBenchlingWorklist;
